import { getPlainTextFromEditorState } from '../../utils/Utils';

/**
 * Finds the first {{...}} block in the given line that contains the cursor position.
 * Returns the block string and its Monaco range for precise hover highlighting.
 *
 * @param {string} lineContent - The content of the current line in the editor.
 * @param {object} position - The Monaco position object (lineNumber, column).
 * @param {object} monaco - The Monaco instance.
 * @returns {object|null} An object with { block, range } or null if not found.
 */
export const findBlockAtPosition = (lineContent, position, monaco) => {
  const blockRegex = /\{\{(?:(?!\}\})[\s\S]){0,5000}\}\}/g;
  let match;
  while ((match = blockRegex.exec(lineContent))) {
    const start = match.index;
    const end = start + match[0].length;
    if (position.column - 1 >= start && position.column - 1 <= end) {
      return {
        block: match[0],
        range: new monaco.Range(
          position.lineNumber,
          start + 1,
          position.lineNumber,
          end + 1
        ),
      };
    }
  }
  return null;
};

/**
 * Extracts hover tooltip content for a {{attributes.ATTRIBUTE_NAME}} block.
 * If the block matches and the attribute exists in the list, returns an array of Markdown hover lines.
 *
 * @param {string} inner - The content inside the {{...}} block.
 * @param {Array} attributeList - List of all available attributes.
 * @returns {Array|null} Array of hover content objects or null if not a valid attribute.
 */
export const getAttributeHover = (inner, attributeList) => {
  const attrMatch = /^attributes\.(.+)$/i.exec(inner);
  if (attrMatch) {
    const attrName = attrMatch[1].trim();
    const attributeMap = new Map(
      attributeList?.map((attr) => [attr?.name, attr])
    );
    if (attributeMap.has(attrName)) {
      const attr = attributeMap.get(attrName);
      const plainTextDescription = attr.description
        ? getPlainTextFromEditorState(attr.description)
        : '';

      return [
        { value: `**Label:** ${attr.label}` },
        { value: `**ID:** ${attr.id}` },
        { value: `**Data Type:** ${attr.data_type}` },
        plainTextDescription
          ? { value: `**Description:** ${plainTextDescription}` }
          : undefined,
      ].filter(Boolean);
    }
  }
  return null;
};

/**
 * Determines if the cursor is inside a {{attributes.ATTRIBUTE_NAME}} block and returns hover info if so.
 * Combines block finding and attribute lookup for use in Monaco hover provider.
 *
 * @param {string} lineContent - The content of the current line in the editor.
 * @param {object} position - The Monaco position object (lineNumber, column).
 * @param {Array} attributeList - List of all available attributes.
 * @param {object} monaco - The Monaco instance.
 * @returns {object|null} Monaco hover object or null if not a valid block/attribute.
 */
export const getFormulaAttributeHover = (
  lineContent,
  position,
  attributeList,
  monaco
) => {
  const blockInfo = findBlockAtPosition(lineContent, position, monaco);
  if (!blockInfo || !blockInfo.block) {
    return null;
  }
  const inner = blockInfo.block.slice(2, -2).trim();
  const contents = getAttributeHover(inner, attributeList);
  if (contents) {
    return {
      range: blockInfo.range,
      contents,
    };
  }
  return null;
};

/**
 * Registers a hover provider for Monaco Editor for the custom-python language.
 * Enables tooltips when hovering over {{attributes.ATTRIBUTE_NAME}} blocks in the editor.
 *
 * @param {object} monaco - The Monaco instance (from the editor)
 * @param {Array} attributeList - List of all available attributes for fast lookup.
 * @returns {object|null} The disposable hover provider.
 */
export const registerFormulaAttributeHoverProvider = (
  monaco,
  attributeList
) => {
  if (monaco && attributeList?.length > 0) {
    return monaco.languages.registerHoverProvider('custom-python', {
      provideHover: (model, position) => {
        const lineContent = model.getLineContent(position.lineNumber);
        return getFormulaAttributeHover(
          lineContent,
          position,
          attributeList,
          monaco
        );
      },
    });
  }
  return null;
};
